import * as config from "./../configuration/index";
import ObjectToString from "./ObjectToString";
import { QueryAttributeOptions } from "./QueryAttributeOptions";
/**
 * Element cache expire time 30 minutes
 */
const DEFAULT_CACHE_EXPIRE_TIME = config.System.query.cacheExpireTime;
let runningId = 1;
export default class QueryAttribute {
  id: string;
  tag: string;
  /**
   * Marks if this attribute was considered as draft
   */
  isDraft?: boolean;
  // in the actual query, the ID can be aliased to some other id
  queryAliasId?: string;
  options: Partial<QueryAttributeOptions> | null = null;
  // when this attribute was last fetched
  cachedTimestamp?: number;
  // stored value of the attribute at last fetch
  cachedValue?: any;
  // the expire time for the cache value
  expiresAt?: number;
  private _signatureWithAs?: string;
  private _signatureWithoutAs?: string;
  constructor(
    tag: string,
    options: Partial<QueryAttributeOptions> | null,
    isClone = false
  ) {
    if (!isClone) {
      runningId += 1;
      const name = options?.as || tag;
      this.id = `${name}_${String(runningId)}`;
      this.queryAliasId = this.id;
      this.tag = tag;
      this.options = options;
    } else {
      this.id = "";
      this.tag = "";
    }
  }
  createCacheEntry(value: any, ms: number) {
    const clone = new QueryAttribute(this.tag, this.options);
    clone.tag = this.tag;
    clone.id = this.id;
    clone.options = this.options;
    clone.queryAliasId = this.queryAliasId;
    clone.cachedValue = value;
    clone.cachedTimestamp = ms;
    clone.expiresAt = ms + DEFAULT_CACHE_EXPIRE_TIME;
    clone.isDraft = this.isDraft;
    return clone;
  }
  /**
   *
   * This will return the name of the element in the networked query
   *
   * @returns string name of the element in the actual query
   */
  getQueryName() {
    if (!this.options) {
      return this.tag;
    }
    return this.queryAliasId ? this.queryAliasId : this.id;
  }
  /**
   * Returns the name of the attribute in the user created query
   *
   * @returns string name of the element in the final result
   */
  getName() {
    if (this.options && this.options.as) {
      return this.options.as;
    }
    return this.tag;
  }
  /**
   * Returns series name of the attribute in the user created query
   *
   * @returns string series name of the element in the final result
   */
  getSeriesName() {
    if (this.options && this.options.series) {
      return this.options.series;
    }
    return "";
  }
  /**
   * This will build the attribute string for the actual MEA query
   *
   * @returns string value in the actual query
   */
  build() {
    if (!this.options) return this.tag;
    const opts = { ...this.options };
    opts.as = this.id;
    if (opts.period && !opts.resolveui2period) {
      opts.resolveui2period = true;
    }
    return `${this.tag}${
      this.options !== null ? `(${ObjectToString(opts)})` : ""
    }`;
  }
  toString() {
    return `${this.tag}${
      this.options !== null ? `(${ObjectToString(this.options)})` : ""
    }`;
  }
  /**
   *
   * Signature, which is used to determine whether two attribute queries are the same
   *
   * @param includeAs boolean, if set to true include "as" name in the signature
   * @returns
   */
  signature(includeAs: boolean = false) {
    if (!this.options) return this.tag;
    if (includeAs) {
      if (this._signatureWithAs) {
        return this._signatureWithAs;
      }
      this._signatureWithAs = this.calculateSignature(includeAs);
      return this._signatureWithAs;
    }
    if (this._signatureWithoutAs) return this._signatureWithoutAs;
    this._signatureWithoutAs = this.calculateSignature(includeAs);
    return this._signatureWithoutAs;
  }
  calculateSignature(includeAs: boolean = false) {
    if (!this.options) return this.tag;
    const sig = `${this.tag}=${Object.entries(this.options)
      .filter(([name]) => includeAs || name !== "as")
      .sort(([name1], [name2]) => name1.localeCompare(name2))
      .map(([name, value]) => {
        if (typeof value === "string") return `${name}=${value}`;
        return `${name}=${JSON.stringify(value)}`;
      })
      .join(",")}`;
    return sig;
  }
}
